/**
 * FihvResponsibilitySplit (responsive HTML/CSS version)
 * Three-lane responsibility diagram: what the tax advisor decides, what the
 * accounting system evidences, and the judgment zone in between.
 *
 * Desktop (>=768px): three-column grid — advisor | judgment | system,
 * with dashed horizontal connectors into the judgment card.
 * Mobile (<768px): single stacked column — advisor, judgment, system —
 * joined by short vertical dashed connectors. Text wraps naturally.
 *
 * Zero hooks — safe as a React Server Component. Icons are lucide-react.
 * Styling is a scoped <style> block (all classes prefixed .fihv-), so no
 * Tailwind or CSS-module wiring is required; the `className` prop is for
 * outer spacing only.
 *
 * Colors are the brand palette (teal blue-500 #0f9993 / blue-800 #0d555c,
 * gold #bda154); font is the app's Poppins.
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import {
  type LucideIcon,
  ClipboardCheck,
  FileText,
  Tag,
  Mail,
  Lock,
  Clock,
  LayoutGrid,
  Folder,
  Scale,
  Check,
} from 'lucide-react';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/** Filled caret protruding from the judgment card — cream fill, gold outline. */
function Caret({ dir }: { dir: 'left' | 'right' }) {
  const pts = dir === 'left' ? '10,0 1,12 10,24' : '0,0 9,12 0,24';
  return (
    <svg width={10} height={24} viewBox="0 0 10 24" aria-hidden="true">
      <polygon points={pts} fill={T.goldBg} />
      <polyline
        points={pts}
        fill="none"
        stroke={T.goldBorder}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

const T = {
  panelBg: '#ffffff',
  panelBorder: '#e5e4de',
  ink: '#1e2a32',
  itemInk: '#163b37',
  itemSub: '#6f7b79',
  cardBg: '#f1f8f7',
  cardBorder: '#dceeec',
  tealDark: '#0d555c', // brand blue-800
  teal: '#0f9993', // brand blue-500 (teal)
  goldBorder: '#bda154', // brand gold
  goldSoft: '#d9c084',
  goldBg: '#fbf7ec',
  goldInk: '#8a6d1f',
  goldText: '#6b5416',
  goldSub: '#97803f',
  goldDivider: '#e5d5a3',
  calloutBg: '#ecf6f3',
  calloutText: '#3d4a48',
};

interface LaneItem {
  icon: LucideIcon;
  title: string;
  sub: string;
}

interface Lane {
  header: string;
  headerBg: string;
  iconStroke: string;
  items: LaneItem[];
}

const ADVISOR: Lane = {
  header: 'Your tax advisor decides',
  headerBg: T.tealDark,
  iconStroke: T.tealDark,
  items: [
    {
      icon: ClipboardCheck,
      title: 'Qualification against the thresholds',
      sub: 'Asset value, Hong Kong headcount, operating expenditure',
    },
    {
      icon: FileText,
      title: 'Drafting & filing the election',
      sub: 'The election lives with the advisor, not the ledger',
    },
    {
      icon: Tag,
      title: 'Transaction classification advice',
      sub: 'What counts as qualifying investment returns',
    },
    {
      icon: Mail,
      title: 'IRD correspondence',
      sub: 'Inquiries, reviews, and everything that follows',
    },
  ],
};

const SYSTEM: Lane = {
  header: 'Your accounting system evidences',
  headerBg: T.teal,
  iconStroke: T.teal,
  items: [
    {
      icon: Lock,
      title: 'Hard period locks',
      sub: 'Closed means closed; overrides are logged events',
    },
    {
      icon: Clock,
      title: 'User-level audit trail',
      sub: 'Who posted what, and when — on every transaction',
    },
    {
      icon: LayoutGrid,
      title: 'Segmented chart of accounts',
      sub: 'Qualifying returns separated from non-qualifying income',
    },
    {
      icon: Folder,
      title: 'Records that support the return',
      sub: 'Kept in a defensible form for the review period',
    },
  ],
};

const CSS = `
.fihv-panel {
  position: relative;
  background: ${T.panelBg};
  border: 1px solid ${T.panelBorder};
  border-radius: 20px;
  padding: clamp(20px, 4vw, 44px) clamp(18px, 4vw, 48px);
  color: ${T.ink};
  font-family: 'Poppins', sans-serif;
}
.fihv-panel .fihv-logo {
  display: none;
}
.fihv-panel .fihv-grid > * { opacity: 0; transform: translateY(14px); transition: opacity .6s ease, transform .6s cubic-bezier(0.19,1,0.22,1); }
.fihv-panel .fihv-grid > *:nth-child(2) { transition-delay: .1s; }
.fihv-panel .fihv-grid > *:nth-child(3) { transition-delay: .2s; }
.fihv-panel .fihv-grid > *:nth-child(4) { transition-delay: .3s; }
.fihv-panel .fihv-grid > *:nth-child(5) { transition-delay: .4s; }
.fihv-panel.fihv-in .fihv-grid > * { opacity: 1; transform: none; }
.fihv-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.22em;
  color: ${T.teal};
  text-transform: uppercase;
}
.fihv-eyebrow::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${T.teal};
  flex-shrink: 0;
}
.fihv-panel .fihv-title {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(22px, 4.2vw, 30px);
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.01em;
  text-transform: none;
  color: ${T.ink};
  margin: 10px 0 48px;
  max-width: 640px;
}
.fihv-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
.fihv-lane {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.fihv-lane-header {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  border-radius: 10px;
  padding: 8px 16px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.25;
  color: #ffffff;
}
.fihv-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${T.cardBg};
  border: 1px solid ${T.cardBorder};
  border-radius: 10px;
  padding: 16px;
}
.fihv-card-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${T.cardBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fihv-panel .fihv-card-title {
  font-size: 15px;
  font-weight: 500;
  color: ${T.itemInk};
  letter-spacing: normal;
  margin: 0 0 4px;
  line-height: 1.35;
}
.fihv-panel .fihv-card-sub {
  font-size: 12px;
  color: ${T.itemSub};
  letter-spacing: normal;
  margin: 0;
  line-height: 1.5;
}
.fihv-judgment-col {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.fihv-judgment-header {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  border: 1.5px dashed ${T.goldBorder};
  border-radius: 10px;
  padding: 8px 16px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.25;
  color: ${T.goldInk};
}
.fihv-judgment-wrap {
  flex: 1;
  display: flex;
  align-items: center;
}
.fihv-judgment-card {
  position: relative;
  width: 100%;
  background: ${T.goldBg};
  border: 1.5px dashed ${T.goldSoft};
  border-radius: 12px;
  padding: 22px 20px;
  text-align: center;
}
.fihv-judgment-icon {
  display: flex;
  justify-content: center;
}
.fihv-panel .fihv-judgment-main {
  font-size: 14px;
  font-weight: 500;
  color: ${T.goldText};
  letter-spacing: normal;
  line-height: 1.45;
  margin: 10px auto 0;
  max-width: 220px;
}
.fihv-panel .fihv-judgment-divider {
  height: 0;
  border: none;
  border-top: 1px solid ${T.goldDivider};
  width: 120px;
  margin: 16px auto;
}
.fihv-panel .fihv-judgment-sub {
  font-size: 12px;
  color: ${T.goldSub};
  letter-spacing: normal;
  line-height: 1.55;
  margin: 0 auto;
  max-width: 220px;
}
.fihv-caret {
  display: none;
}
.fihv-vconn {
  width: 0;
  height: 22px;
  border-left: 1.5px dashed ${T.goldSoft};
  margin: 0 auto;
  position: relative;
}
.fihv-vconn::before,
.fihv-vconn::after {
  content: '';
  position: absolute;
  left: -4.25px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${T.goldSoft};
}
.fihv-vconn::before { top: -3px; }
.fihv-vconn::after { bottom: -3px; }
.fihv-callout {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: transparent;
  border: 1px solid ${T.panelBorder};
  border-radius: 12px;
  padding: 15px 18px;
  margin-top: 24px;
  font-size: 14px;
  line-height: 1.55;
  color: ${T.calloutText};
}
.fihv-panel .fihv-callout p {
  margin: 0;
  font-size: 14px;
  letter-spacing: normal;
  line-height: 1.55;
  color: ${T.calloutText};
}
.fihv-panel .fihv-callout strong {
  color: ${T.tealDark};
  font-weight: 600;
}
.fihv-callout-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${T.teal};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
@media (min-width: 768px) {
  .fihv-grid {
    grid-template-columns: 1fr 0.62fr 1fr;
    gap: 24px;
  }
  .fihv-vconn {
    display: none;
  }
  .fihv-caret {
    display: block;
    position: absolute;
    top: 50%;
    line-height: 0;
  }
  .fihv-caret-left {
    right: 100%;
    transform: translate(3px, -50%);
  }
  .fihv-caret-right {
    left: 100%;
    transform: translate(-3px, -50%);
  }
  .fihv-panel .fihv-logo {
    display: block;
    position: absolute;
    top: clamp(20px, 4vw, 44px);
    right: clamp(18px, 4vw, 48px);
    height: 40px;
    width: auto;
    margin: 0;
  }
  .fihv-panel .fihv-title {
    padding-right: 140px;
  }
}
@media (max-width: 767px) {
  .fihv-panel {
    border-left: none;
    border-right: none;
    border-radius: 0;
    padding-top: 30px;
    padding-bottom: 30px;
    padding-left: 0;
    padding-right: 0;
  }
  .fihv-eyebrow::before {
    display: none;
  }
}
`;

function LaneBlock({ lane }: { lane: Lane }) {
  return (
    <div className="fihv-lane">
      <div className="fihv-lane-header" style={{ background: lane.headerBg }}>
        {lane.header}
      </div>
      {lane.items.map((item) => {
        const ItemIcon = item.icon;
        return (
          <div className="fihv-card" key={item.title}>
            <span className="fihv-card-icon">
              <ItemIcon size={18} strokeWidth={1.8} color={lane.iconStroke} aria-hidden="true" />
            </span>
            <div>
              <p className="fihv-card-title">{item.title}</p>
              <p className="fihv-card-sub">{item.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface FihvResponsibilitySplitProps {
  className?: string;
}

function useInView(ref: { readonly current: HTMLDivElement | null }): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

export default function FihvResponsibilitySplit({ className }: FihvResponsibilitySplitProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const inView = useInView(panelRef);
  return (
    <section
      className={className}
      aria-label="FIHV concession: division of responsibility between your tax advisor and your accounting system"
    >
      <style>{CSS}</style>
      <div className={`fihv-panel${inView ? ' fihv-in' : ''}`} ref={panelRef}>
        <img className="fihv-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
        <div className="fihv-eyebrow">FIHV concession &middot; Division of responsibility</div>
        <h3 className="fihv-title">
          Who owns what: the advisor, the system, and the judgment in between.
        </h3>

        <div className="fihv-grid">
          <LaneBlock lane={ADVISOR} />

          <div className="fihv-vconn" aria-hidden="true" />

          <div className="fihv-judgment-col">
            <div className="fihv-judgment-header">Judgment, not software</div>
            <div className="fihv-judgment-wrap">
              <div className="fihv-judgment-card">
                <span className="fihv-caret fihv-caret-left" aria-hidden="true">
                  <Caret dir="left" />
                </span>
                <span className="fihv-caret fihv-caret-right" aria-hidden="true">
                  <Caret dir="right" />
                </span>
                <span className="fihv-judgment-icon">
                  <Scale size={30} strokeWidth={1.8} color={T.goldInk} aria-hidden="true" />
                </span>
                <p className="fihv-judgment-main">
                  Whether a transaction actually sits in a qualifying category
                </p>
                <hr className="fihv-judgment-divider" />
                <p className="fihv-judgment-sub">
                  Structure makes the call easier. It doesn&rsquo;t make the call.
                </p>
              </div>
            </div>
          </div>

          <div className="fihv-vconn" aria-hidden="true" />

          <LaneBlock lane={SYSTEM} />
        </div>

        <div className="fihv-callout">
          <span className="fihv-callout-icon">
            <Check size={16} strokeWidth={2.4} color="#ffffff" aria-hidden="true" />
          </span>
          <p style={{ margin: 0 }}>
            <strong>The system is a supporting document.</strong> It isn&rsquo;t the decision-maker,
            the filing system, or the threshold monitor.
          </p>
        </div>
      </div>
    </section>
  );
}
